import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { MyLocationState } from '../../shared/place_search/my_location_row.js';
import { useBrowserPosition, type BrowserPosition } from '../../shared/use_browser_position.js';
import type { RentalPlaceField, RentalSearchPlace, RentalSearchValue } from '../models.js';

type MyLocationRequest = { field: RentalPlaceField; state: MyLocationState };

/** O lugar REAL no ponto (o mesmo ref de um pick); `null` quando o ponto não
 *  geocodifica. Lança com o serviço fora — o campo mostra o erro do mesmo jeito. */
export type RentalPlaceAt = (
  position: BrowserPosition,
) => Promise<Pick<RentalSearchPlace, 'id' | 'label'> | null>;

/**
 * "Perto de mim" da `RentalSearchBar` (#2441), dos dois hosts dela — a bancada e
 * o cartão da conversa (#5641): a posição do navegador vira um lugar real pelo
 * `placeAt` do host e entra no campo com `source: 'geolocation'` — o mesmo ref
 * de um pick, e a marca que tira a busca das recentes. Um pick da barra grava o
 * lugar sem ela. Recusa do navegador, ponto sem lugar e serviço fora caem todos
 * no `error` da linha do campo que pediu; o campo segue usável por texto. Um
 * clique mais novo vence um mais velho ainda no ar — e um pick MANUAL no campo
 * que pediu também (`onValueChange`): a espera inclui o prompt de permissão do
 * navegador, que pode levar segundos, e a posição que chegasse depois
 * sobrescreveria a escolha da pessoa. Só o lugar DAQUELE campo invalida: mexer
 * em data ou no modo de devolução não cancela o pedido em voo. `onFill` troca os
 * lugares por outra origem (um recente): cancela a posição em voo. Sem
 * `placeAt`, não há "perto de mim" (`onUseMyLocation` ausente).
 */
export function useRentalMyLocation(
  value: RentalSearchValue,
  setValue: Dispatch<SetStateAction<RentalSearchValue>>,
  placeAt: RentalPlaceAt | undefined,
): {
  myLocation: MyLocationRequest | undefined;
  onUseMyLocation: ((field: RentalPlaceField) => void) | undefined;
  onValueChange: (next: RentalSearchValue) => void;
  onFill: (next: RentalSearchValue) => void;
} {
  const position = useBrowserPosition();
  const readPosition = position.read;
  const [request, setRequest] = useState<MyLocationRequest>();
  const seq = useRef(0);
  const requestPlace = useCallback(
    async (field: RentalPlaceField, resolve: RentalPlaceAt) => {
      const mine = ++seq.current;
      setRequest({ field, state: 'loading' });
      const read = await readPosition();
      let place: Pick<RentalSearchPlace, 'id' | 'label'> | null = null;
      if (read.kind === 'position') {
        try {
          place = await resolve(read.position);
        } catch {
          place = null;
        }
      }
      if (seq.current !== mine) return;
      if (!place) {
        // A permissão recusada ganha a dica de onde liberar (#4513, I6).
        setRequest({ field, state: read.kind === 'denied' ? 'denied' : 'error' });
        return;
      }
      const located = { id: place.id, label: place.label, source: 'geolocation' as const };
      setValue((prev) =>
        field === 'pickup' ? { ...prev, pickupPlace: located } : { ...prev, dropoffPlace: located },
      );
      setRequest(undefined);
    },
    [readPosition, setValue],
  );
  const onFill = useCallback(
    (next: RentalSearchValue) => {
      seq.current += 1;
      setRequest(undefined);
      setValue(next);
    },
    [setValue],
  );
  return {
    myLocation: request,
    onUseMyLocation: placeAt
      ? (field) => {
          void requestPlace(field, placeAt);
        }
      : undefined,
    onValueChange: (next) => {
      const key = request?.field === 'pickup' ? 'pickupPlace' : 'dropoffPlace';
      // Por REFERÊNCIA, não por id: todo pick da barra cria o lugar de novo, e
      // datas ou modo de devolução repassam o mesmo objeto. Assim reescolher o
      // MESMO lugar durante a espera também vence a posição.
      if (request && next[key] !== value[key]) {
        seq.current += 1;
        setRequest(undefined);
      }
      setValue(next);
    },
    onFill,
  };
}
